import React, { useEffect, useState } from 'react'
import { useI18n } from '@ggs/i18n'
import { get, uniq, sortBy, groupBy, size, toString, last, without, join } from 'lodash'
import { Link } from '@mui/material'
import { DataTable } from '../'
import { SelectOptionControl } from '../../forms'
import { Loader } from '../../loader'
import { useAcademicApi, useAcademicDataDigest, useAcademicExpApi } from '../../../local'
import { useSelector } from '@ggs/store'
import { getWindow } from '@ggs/utils'

import './SubjectCoursesTable.scss'

/**
 * @typedef {import('../../../definitions').CourseItem} CourseItem
 * @typedef {import('../../../definitions').SubjectCourseList} SubjectCourseList
 * @typedef {import('../../../definitions').SelectOption} SelectOption
 * @typedef {import('lodash').Dictionary<CourseItem[]>} SubjectCoursesIndex
 * @typedef {import('mui-datatables').MUIDataTableOptions} MUIDataTableOptions
 */

const tableColumns = [
  {
    name: 'courseCodeLink',
    label: 'Course Code',
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: 'courseTitleLink',
    label: 'Course Title',
    options: {
      filter: false,
      sort: false,
    },
  },
]

/**
 * Course subject courses table.
 * @return {JSX.Element}
 */
const SubjectCoursesTable = () => {
  const { t } = useI18n()
  const { getCourseSections } = useAcademicExpApi()
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'
  const [dataTable, setDataTable] = useState([])
  const [loading, setLoading] = useState(true)
  const campus = useSelector((state) => state.settings.campus || 'UBCV')
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)
  const courseSubjectId = get(window, 'drupalSettings.react.subjectCourseId', 'ENGL_V')

  const [courseLevelFilterOptions, setCourseLevelFilterOptions] = useState([])
  const [activeCourseLevelFilter, setActiveCourseLevelFilter] = useState('')
  const { digestSubjectCoursesList } = useAcademicDataDigest()
  const pageSize = 500

  const enableLoading = () => setLoading(true)
  const disableLoading = () => setLoading(false)

  /**
   * Handle click on course title.
   * @param {React.MouseEvent<HTMLAnchorElement>} e
   */
  const handleCourseTitleClick = (e) => {
    // Prevent opening target link when running in SB.
    if (isStorybook) {
      e.preventDefault()
      alert('Course detail route will open when running in Drupal.')
    }
  }

  /**
   * Compose unique course level options for the filter dropdown.
   * @param {SubjectCourseList} dataTable
   * @return {SelectOption[]}
   */
  const composeCourseLevelOptions = (dataTable) => {
    if (!dataTable) return null

    const courseLevels = dataTable.map((item) => item.courseLevel)
    const uniqueCourseLevels = sortBy(uniq(courseLevels))
    const options = [
      { label: t('All'), value: '' },
      ...uniqueCourseLevels.map((level) => ({ label: level, value: level })),
    ]
    return options
  }

  /**
   * Augment data table with links to course detail page.
   * @param {SubjectCourseList} dataTable
   * @return {SubjectCourseList}
   */
  const augmentWithDerivativeFields = (dataTable) => {
    return dataTable.map((item) => ({
      ...item,
      courseTitleLink: (
        <Link onClick={handleCourseTitleClick} href={`/browse-courses/course/${item.courseInstanceId}`}>
          {item.courseTitle}
        </Link>
      ),
      courseCodeLink: (
        <Link onClick={handleCourseTitleClick} href={`/browse-courses/course/${item.courseInstanceId}`}>
          {item.courseCode}
        </Link>
      ),
    }))
  }

  const resolveSubjectCoursesData = (page = 1) => {
    enableLoading()
    getCourseSections({
      page,
      pageSize,
      academicPeriodId: [academicPeriod],
      courseSubject: courseSubjectId,
    }).then((apiData) => {
      const pageDataTable = digestSubjectCoursesList(apiData.pageItems)
      const courseLevelOptions = composeCourseLevelOptions(pageDataTable)
      setCourseLevelFilterOptions(courseLevelOptions)
      const newDataTable = augmentWithDerivativeFields(pageDataTable)
      setDataTable(newDataTable)
      disableLoading()
    })
  }

  useEffect(() => {
    resolveSubjectCoursesData()
  }, [academicPeriod])

  /**
   * Handle course level option change.
   * @param {string} courseLevel
   */
  const handleCourseLevelSelection = (courseLevel) => {
    setActiveCourseLevelFilter(courseLevel)
  }

  /**
   * @type {MUIDataTableOptions}
   */
  const options = {
    jumpToPage: false,
    pagination: false,
    rowsPerPage: pageSize,
    textLabels: {
      body: {
        noMatch: loading ? t('Please wait...') : t('No course sections offered this period'),
      },
    },
  }

  if (loading) return <Loader />

  const finalDataTable = groupBy(dataTable, 'courseLevel')[activeCourseLevelFilter] || dataTable

  return (
    <div className="subjects-courses">
      {size(courseLevelFilterOptions) > 1 && (
        <div className="subjects-courses__filters">
          <SelectOptionControl
            id="page-index"
            options={courseLevelFilterOptions}
            defaultValue={activeCourseLevelFilter}
            selectLabel={t('Filter Course Levels')}
            onSelectOption={handleCourseLevelSelection}
          />
        </div>
      )}
      <div className="subjects-courses__table">
        {finalDataTable.length > 0 ? (
          <DataTable
            className="data-table"
            title=""
            data={finalDataTable}
            columns={tableColumns}
            options={{
              ...options,
              count: pageSize,
            }}
          />
        ) : (
          <div className="subjects-courses__no-courses-message">
            <h3>{t('No course sections offered this period.')}</h3>
          </div>
        )}
      </div>
    </div>
  )
}

export default SubjectCoursesTable
